import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { type TColumns, useSortColumnsModal } from 'hooks/useSortColumnsModal';
import type { TObjectJobSummary } from 'types/objectJob.type';
import { EditableTable } from '@timeedit/ui-components';
import {
  ConfigProvider,
  type TablePaginationConfig,
  type TableProps,
  type ThemeConfig,
  theme,
} from '@timeedit/ui-components/antd';
import { JobActions } from './JobActions';
import { JobStatus } from './JobStatus';
import { useFetchObjectJobListQuery } from './api/objectJobApi';
import { jobOperationTypeLabels } from './constants/job.constant';

type ColumnKey =
  | 'id'
  | 'queuedAt'
  | 'operationType'
  | 'objectTypeName'
  | 'userNameOrEmail'
  | 'status'
  | 'actions';

const GENERAL_COLUMN_WIDTH = 150;
const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_SORT = 'queuedAt:desc';

const formatQueuedAt = (value?: string) => {
  if (!value) {
    return 'N/A';
  }

  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm:ss') : 'N/A';
};

const toSortBy = (
  sorter: Parameters<TableProps<TObjectJobSummary>['onChange']>[2]
) => {
  const nextSorter = Array.isArray(sorter) ? sorter[0] : sorter;

  if (!nextSorter?.order || !nextSorter.field) {
    return DEFAULT_SORT;
  }

  const direction = nextSorter.order === 'ascend' ? 'asc' : 'desc';
  return `${nextSorter.field}:${direction}`;
};

export const JobTable = () => {
  const { token } = theme.useToken();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
  const [sortBy, setSortBy] = useState(DEFAULT_SORT);

  const { data, isFetching } = useFetchObjectJobListQuery(
    {
      page,
      limit,
      sortBy,
    },
    { refetchOnFocus: true }
  );
  const columns: TColumns<ColumnKey, TObjectJobSummary> = useMemo(() => {
    return {
      id: {
        label: 'Job ID',
        key: 'id',
        width: GENERAL_COLUMN_WIDTH,
        sorter: true,
      },
      queuedAt: {
        label: 'Start Time',
        key: 'queuedAt',
        width: GENERAL_COLUMN_WIDTH,
        sorter: true,
        render: (value: string) => formatQueuedAt(value),
      },
      operationType: {
        label: 'Operations',
        key: 'operationType',
        width: GENERAL_COLUMN_WIDTH,
        sorter: true,
        render: (value: string) => jobOperationTypeLabels[value] ?? value,
      },
      objectTypeName: {
        label: 'Object Type',
        key: 'objectTypeName',
        width: GENERAL_COLUMN_WIDTH,
        sorter: true,
      },
      userNameOrEmail: {
        label: 'User',
        key: 'userNameOrEmail',
        width: GENERAL_COLUMN_WIDTH,
        sorter: true,
        render: (value: string | null) => <span>{value ?? 'N/A'}</span>,
      },
      status: {
        label: 'Status',
        key: 'status',
        width: 220,
        sorter: true,
        render: (_, record) => <JobStatus job={record} />,
      },
      actions: {
        label: '',
        key: 'actions',
        width: GENERAL_COLUMN_WIDTH,
        render: (_, job) => <JobActions job={job} />,
      },
    };
  }, []);

  const { sortedColumns } = useSortColumnsModal<ColumnKey, TObjectJobSummary>(
    columns
  );

  const pagination = useMemo<TablePaginationConfig>(
    () => ({
      current: page,
      onChange: setPage,
      total: data?.totalResults || 0,
      pageSize: limit,
      showSizeChanger: false,
    }),
    [page, limit, data?.totalResults]
  );

  const handleTableChange: TableProps<TObjectJobSummary>['onChange'] = (
    _pagination,
    _filters,
    sorter
  ) => {
    setSortBy(toSortBy(sorter));
  };

  const themeOverride: ThemeConfig = useMemo(() => {
    const override = {
      components: {
        Table: {
          rowExpandedBg: token.colorBgBase,
          rowHoverBg: token.colorFillSecondary,
          headerBg: token.colorBgBase,
        } as Record<string, string>,
      },
    };

    return override;
  }, [token.colorBgBase, token.colorFillSecondary]);

  return (
    <ConfigProvider theme={themeOverride}>
      <EditableTable
        columns={sortedColumns}
        dataSource={data?.results || []}
        loading={isFetching}
        pagination={pagination}
        onChange={handleTableChange}
        rowKey="id"
      />
    </ConfigProvider>
  );
};
